import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Select, Checkbox, message } from 'antd';
import { ChevronLeftIcon } from '../../assets/icons';
import { BIND_CHIP, BIND_LABEL, isOwn } from '../admin/platform/mockData';
import {
  useGetClientContextQuery,
  useGetClientTerritoriesQuery,
  useGetStateDepartmentQuery,
  type StateDeptLevel,
} from '../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Client Admin — one department (prototype `builderVals`, client mode).
 * Read-only view of the state (Rajasthan) profile the client inherits:
 * levels + bindings + coverage, a selected-level panel, village lookup
 * and validation. The client can only request changes, not edit.
 * Route: /client/departments/:code/view
 * ------------------------------------------------------------------ */

const covText = (c: number) => (c >= 100 ? 'text-brand' : c >= 70 ? 'text-[#8a4b0f]' : 'text-[#b42318]');
const covBar = (c: number) => (c >= 100 ? 'bg-brand' : c >= 70 ? 'bg-[#8a4b0f]' : 'bg-[#b42318]');

/** Representative in-boundary unit counts for anchored/inherited levels (from LGD). */
const BOUNDARY_COUNT: Record<string, number> = {
  State: 1,
  Division: 1,
  District: 3,
  'Sub-division': 9,
  Tehsil: 17,
  Village: 1846,
  Booth: 1846,
};

export default function ClientDepartmentViewPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const tcode = code ?? '';

  // The client's state comes from the logged-in user's org (no hardcoded RJ).
  const { data: ctx } = useGetClientContextQuery();
  const clientState = ctx?.state_code ?? '';
  const STATE_NAME = ctx?.state_name ?? 'your state';

  const { data: dept, isLoading, isError } = useGetStateDepartmentQuery(
    { code: clientState, tcode },
    { skip: !tcode || !clientState },
  );
  const { data: territories = [] } = useGetClientTerritoriesQuery();

  const [selIdx, setSelIdx] = useState(0);
  const [accept, setAccept] = useState(false);

  const levels = useMemo<StateDeptLevel[]>(() => dept?.levels ?? [], [dept]);

  const terrMap = useMemo(() => {
    const m = new Map<string, { units: number; coverage: number | null }>();
    territories
      .filter((t) => t.template_code === tcode)
      .forEach((t) => m.set(t.level, { units: t.units, coverage: t.coverage }));
    return m;
  }, [territories, tcode]);

  if (isLoading && !dept) return <p className="text-sm text-ink-secondary">Loading…</p>;
  if (isError || !dept) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-secondary">Department not found.</p>
        <button
          type="button"
          onClick={() => navigate('/client/departments')}
          className="inline-flex h-10 w-fit items-center gap-1.5 rounded-lg border border-line bg-white pl-2.5 pr-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
        >
          <ChevronLeftIcon className="h-3 w-[7px]" />
          All hierarchies
        </button>
      </div>
    );
  }

  const districtIdx = levels.findIndex((l) => l.name === 'District');
  const beyondOf = (i: number) => districtIdx > 0 && i < districtIdx;
  const covOf = (l: StateDeptLevel) =>
    l.binding === 'anchor' || l.binding === 'inherited' ? 100 : terrMap.get(l.name)?.coverage ?? 76;
  const countOf = (l: StateDeptLevel) =>
    isOwn(l.binding) ? terrMap.get(l.name)?.units ?? 0 : BOUNDARY_COUNT[l.name] ?? 1;

  const anchorAbove = (idx: number) => {
    for (let j = idx - 1; j >= 0; j -= 1) if (levels[j].binding === 'anchor') return levels[j].name;
    return '';
  };
  const sourceOf = (l: StateDeptLevel, i: number) => {
    if (l.binding === 'anchor') return `LGD › ${l.name}`;
    if (l.binding === 'inherited') return `from ${anchorAbove(i) || 'anchor'} · LGD › ${l.name}`;
    if (l.binding === 'village') return 'villages within one tehsil';
    if (l.binding === 'booth') return 'booths within one unit';
    if (l.binding === 'facility' || l.binding === 'none') return 'listed one by one';
    // grouped / own
    const next = levels[i + 1];
    return `groups of LGD › ${next ? next.name : 'units'}`;
  };

  const anchors = levels.filter((l) => l.binding === 'anchor').length;
  const inherited = levels.filter((l) => l.binding === 'inherited').length;
  const own = levels.filter((l) => isOwn(l.binding)).length;
  const ownCovs = levels.filter((l) => isOwn(l.binding) && !beyondOf(levels.indexOf(l))).map((l) => covOf(l));
  const minCov = ownCovs.length ? Math.min(...ownCovs) : 100;

  const anchorNames = levels.filter((l) => l.binding === 'anchor').map((l) => l.name);
  const inheritedNames = levels.filter((l) => l.binding === 'inherited').map((l) => l.name);
  const ownNames = levels.filter((l) => isOwn(l.binding) && !beyondOf(levels.indexOf(l))).map((l) => l.name);
  const desc =
    `Anchored to LGD at ${anchorNames.join(' and ') || 'the base'}, so ${inheritedNames.join(' and ') || 'lower levels'} ` +
    `come in automatically. ${ownNames.join(' and ') || 'Own levels'} are own levels inserted into that inherited spine.`;

  const sel = levels[selIdx] ?? levels[0];
  const nextLvl = levels[selIdx + 1];
  const dependents = levels
    .map((l, i) => ({ l, i }))
    .filter((x) => x.l.binding === 'inherited' && anchorAbove(x.i) === sel?.name)
    .map((x) => x.l.name);
  const inheritsBelow = dependents.length
    ? dependents.join(', ')
    : nextLvl
      ? `none (${nextLvl.name} below is own)`
      : 'none (lowest level)';

  const requestChange = () => {
    message.success('Change request sent to the platform team.');
  };

  const SRC_OPTS = ['LGD › State', 'LGD › District', 'LGD › Sub-district', 'ECI › Assembly Constituency', 'ECI › Polling station'];
  const lookupValue = (l: StateDeptLevel) => {
    if (isOwn(l.binding) && (terrMap.get(l.name)?.coverage ?? 0) < 100 && l.name !== 'Division' && l.name !== 'Sub-division') {
      // own levels resolve once mapped; show representative names where known
    }
    const map: Record<string, string> = {
      State: STATE_NAME,
      Division: 'Jaipur Division',
      District: 'Alwar',
      'Sub-division': 'Rajgarh SD',
      Tehsil: 'Reni',
      'Patwar Circle': 'Reni circle 1',
      Village: 'Karanpura',
      Booth: 'Karanpura',
    };
    return map[l.name] ?? (isOwn(l.binding) ? '— not resolved yet' : '—');
  };

  const gaps = levels
    .filter((l, i) => isOwn(l.binding) && !beyondOf(i) && covOf(l) < 100)
    .map((l) => `${l.name}: ${100 - covOf(l)}% of villages in the boundary are not mapped yet.`);
  const hasBeyond = levels.some((l, i) => beyondOf(i));

  const stats = [
    { label: 'Anchors', value: anchors, cls: 'text-brand' },
    { label: 'Inherited', value: inherited, cls: 'text-brand-dark' },
    { label: 'Own levels', value: own, cls: 'text-[#73300b]' },
    { label: 'Village coverage', value: `${minCov}%`, cls: covText(minCov) },
  ];

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Back */}
      <div>
        <button
          type="button"
          onClick={() => navigate('/client/departments')}
          className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-line bg-white pl-2.5 pr-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
        >
          <ChevronLeftIcon className="h-3 w-[7px]" />
          All hierarchies
        </button>
      </div>

      {/* Header */}
      <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-6 shadow-card">
        <span className="text-xs text-ink-secondary">
          Template: {dept.name} · {STATE_NAME} profile · shared across {STATE_NAME} clients
        </span>
        <h1 className="text-2xl font-bold text-ink">{dept.name}</h1>
        <p className="max-w-[820px] text-sm leading-6 text-ink-secondary">{desc}</p>
        {dept.minister && (
          <p className="text-[13px] text-ink">
            <b>Minister in charge:</b> {dept.minister}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2.5 pt-1">
          {stats.map((st) => (
            <div key={st.label} className="min-w-[120px] rounded-lg border border-line/50 px-3.5 py-2">
              <div className="text-xs text-ink-secondary">{st.label}</div>
              <div className={['text-[22px] font-bold', st.cls].join(' ')}>{st.value}</div>
            </div>
          ))}
          <span className="self-start rounded-full bg-muted-bg px-3 py-1 text-xs font-semibold text-[#434655]">
            Provided by the {STATE_NAME} profile
          </span>
        </div>
      </section>

      <div className="flex flex-col gap-[18px] lg:flex-row">
        {/* Levels table */}
        <section className="min-w-0 flex-1 rounded-xl border border-line/30 bg-white shadow-card">
          <div className="flex flex-wrap items-baseline justify-between gap-2 p-5 pb-2">
            <h2 className="text-base font-bold text-ink">Levels, top to bottom</h2>
            <span className="text-xs text-ink-secondary">Select a level to see how it is built</span>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[680px]">
              <div className="grid grid-cols-[28px_minmax(150px,1.3fr)_130px_minmax(180px,1.6fr)_96px_140px] gap-3.5 border-y border-[#f3f4f6] bg-[#f9fafb] px-[18px] py-2 text-[11px] uppercase tracking-[0.05em] text-ink-secondary">
                <span />
                <span>Level</span>
                <span>Binding</span>
                <span>Source / members</span>
                <span>In boundary</span>
                <span>Coverage</span>
              </div>
              {levels.map((l, i) => {
                const cov = covOf(l);
                const on = i === selIdx;
                const beyond = beyondOf(i);
                const inserted = isOwn(l.binding) && !beyond;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelIdx(i)}
                    className={[
                      'grid w-full grid-cols-[28px_minmax(150px,1.3fr)_130px_minmax(180px,1.6fr)_96px_140px] items-center gap-3.5 border-b border-[#f3f4f6] px-[18px] py-3.5 text-left transition-colors',
                      on ? 'bg-brand-shade/50 shadow-[inset_3px_0_0_#0061ff]' : 'hover:bg-brand-bg',
                    ].join(' ')}
                  >
                    <span className={['block size-3 rounded-full', BIND_CHIP[l.binding].split(' ')[0]].join(' ')} aria-hidden />
                    <span className="flex flex-col items-start gap-1">
                      <span className="font-bold text-ink">{l.name}</span>
                      {beyond && (
                        <span className="rounded border border-line px-1.5 text-[11px] text-ink-secondary">extends beyond boundary</span>
                      )}
                      {inserted && (
                        <span className="rounded bg-[#fbeadb] px-1.5 text-[11px] font-semibold text-[#73300b]">inserted</span>
                      )}
                    </span>
                    <span>
                      <span className={['inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold', BIND_CHIP[l.binding]].join(' ')}>
                        {l.binding === 'anchor' ? '⚓ ' : ''}
                        {l.binding_label || BIND_LABEL[l.binding]}
                      </span>
                    </span>
                    <span className="text-xs text-[#434655]">{sourceOf(l, i)}</span>
                    <span className="text-[13px] text-ink">{countOf(l).toLocaleString()}</span>
                    <span className="flex flex-col gap-1">
                      <span className="block h-1.5 overflow-hidden rounded-full bg-muted-bg">
                        <span className={['block h-1.5 rounded-full', covBar(cov)].join(' ')} style={{ width: `${cov}%` }} />
                      </span>
                      <span className={['text-xs', covText(cov)].join(' ')}>
                        {cov}%{beyond ? ' · in boundary' : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Right column */}
        <div className="flex w-full flex-col gap-[18px] lg:w-[360px] lg:shrink-0">
          {sel && (
            <aside className="flex flex-col gap-3 rounded-xl border border-line/30 bg-white p-5 shadow-card">
              <div>
                <div className="text-[11px] uppercase tracking-[0.05em] text-ink-secondary">
                  Level {selIdx + 1} of {levels.length}
                </div>
                <h3 className="text-xl font-bold text-ink">{sel.name}</h3>
                <div className="text-[13px] text-ink-secondary">
                  Post at this level: <b className="text-ink">{sel.official_post || 'Set in the department’s designations'}</b>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-ink-secondary">Binding</span>
                <span className={['inline-flex w-fit items-center rounded-full px-3 py-1.5 text-xs font-semibold', BIND_CHIP[sel.binding]].join(' ')}>
                  {sel.binding === 'anchor' ? '⚓ ' : ''}
                  {sel.binding_label || BIND_LABEL[sel.binding]}
                </span>
              </div>

              <div className="flex flex-col gap-2 rounded-lg border border-line/50 bg-brand-bg/50 p-3 text-[13px] text-ink-secondary">
                Set by the {STATE_NAME} profile. Clients choose which levels to use, not how they are built.
                <button
                  type="button"
                  onClick={requestChange}
                  className="h-9 w-fit rounded-lg border border-brand bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
                >
                  Request a change
                </button>
              </div>

              {sel.binding === 'anchor' && (
                <div className="flex flex-col gap-2 rounded-lg bg-brand-shade/60 p-3">
                  <span className="text-xs font-semibold text-brand-dark">Same as this level of an existing hierarchy</span>
                  <div className="form-field-select">
                    <Select className="w-full" value={`LGD › ${sel.name}`} open={false} options={SRC_OPTS.map((o) => ({ value: o, label: o }))} />
                  </div>
                  <span className="text-[13px] text-ink">
                    Levels inherited below: <b>{inheritsBelow}</b>
                  </span>
                  <span className="text-xs text-[#434655]">
                    Territories are references to the source, so there is nothing to enter. When the source
                    changes, inherited levels update and affected own groupings are flagged.
                  </span>
                </div>
              )}
              {sel.binding === 'inherited' && (
                <div className="rounded-lg border border-dashed border-[#7fa3e8] bg-brand-shade/40 p-3 text-[13px] text-ink-secondary">
                  Inherited from the anchor above. Resolved automatically from the source.
                </div>
              )}
              {isOwn(sel.binding) && (
                <div className="rounded-lg border border-[#ebc9ae] bg-[#fdf4ec] p-3 text-[13px] text-[#73300b]">
                  Own level, made of a chosen member level. If villages are missing, request the platform
                  team to update the shared mapping.
                </div>
              )}

              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between border-b border-[#f3f4f6] pb-1.5">
                  <span className="text-xs font-semibold text-ink-secondary">Units in boundary</span>
                  <span className="text-xs text-ink-secondary">{countOf(sel).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between gap-2 py-1.5">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-ink">{lookupValue(sel)}</div>
                    <div className="text-xs text-ink-secondary">LGD {sel.name.toLowerCase()}</div>
                  </div>
                  <span className={['text-xs font-semibold', covText(covOf(sel))].join(' ')}>{covOf(sel)}%</span>
                </div>
              </div>
            </aside>
          )}

          {/* Village lookup */}
          <aside className="flex flex-col gap-2.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
            <div>
              <h3 className="text-base font-bold text-ink">Village lookup</h3>
              <div className="text-[13px] text-ink-secondary">
                <b className="text-ink">Karanpura (sample village)</b> resolves to, in this hierarchy:
              </div>
            </div>
            <div className="flex flex-col">
              {levels.map((l, i) => {
                const val = lookupValue(l);
                const unresolved = val.startsWith('—');
                return (
                  <div key={i} className="grid grid-cols-[16px_minmax(90px,1fr)_minmax(0,1fr)] items-center gap-2.5 border-b border-[#f3f4f6] py-1.5 last:border-b-0">
                    <span className={['block size-3 rounded-full', BIND_CHIP[l.binding].split(' ')[0]].join(' ')} aria-hidden />
                    <span className="text-xs text-ink-secondary">{l.name}</span>
                    <span className={['text-[13px] font-semibold', unresolved ? 'text-[#b42318]' : 'text-ink'].join(' ')}>{val}</span>
                  </div>
                );
              })}
            </div>
            <span className="text-xs text-ink-secondary">
              The same village resolves in every other hierarchy too. That is how a Tehsil chosen in
              Revenue finds its Police Station.
            </span>
          </aside>
        </div>
      </div>

      {/* Validation */}
      <section className="flex flex-col gap-2 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-bold text-ink">Validation</h2>
          <span className="text-xs text-ink-secondary">
            {gaps.length ? `${gaps.length} gap${gaps.length > 1 ? 's' : ''} to resolve or accept` : 'No gaps. Ready to activate'}
          </span>
        </div>
        {gaps.map((g) => (
          <div key={g} className="flex items-baseline gap-2.5 border-t border-[#f3f4f6] py-2 text-[13px]">
            <span className="rounded bg-[#fee2e2] px-2 py-0.5 text-[11px] font-semibold text-[#dc2626]">GAP</span>
            <span className="text-ink">{g}</span>
          </div>
        ))}
        {hasBeyond && (
          <div className="flex items-baseline gap-2.5 border-t border-[#f3f4f6] py-2 text-[13px]">
            <span className="rounded bg-muted-bg px-2 py-0.5 text-[11px] font-semibold text-[#434655]">INFO</span>
            <span className="text-ink">
              State and Division extend beyond the boundary. Each is kept as a single node so escalation
              still reaches the top.
            </span>
          </div>
        )}
        <div className="flex items-start gap-2 border-t border-[#f3f4f6] pt-2 text-[13px] text-ink">
          <Checkbox checked={accept} onChange={() => setAccept((v) => !v)} />
          <span>
            Activate with known gaps. Unmapped villages fall back to the nearest mapped level above and
            stay listed here.
          </span>
        </div>
      </section>
    </div>
  );
}
